/**
 * Organization Management API Service
 * Provides methods for managing organizations (law firms).
 * All operations require authentication.
 */

import { ApiService } from './api';
import type {
  Organization,
  OrganizationCreate,
  OrganizationUpdate,
  OrganizationWithCounts,
  PaginatedOrganizations,
  OrganizationUser,
  OrganizationMember,
  OrganizationTenant,
  OrganizationListParams,
  OrganizationPublicInfo,
  CreateOrgUserRequest,
  CreatedOrgUser,
  ResendInviteResponse,
  UpdateOrgUserRequest,
} from '../types/organization';

/**
 * Organization Management API Client
 */
class OrganizationApiServiceClass {
  /**
   * Get base URL for organization endpoints.
   */
  private getBaseUrl(): string {
    return '/api/organizations';
  }

  /**
   * List all organizations with pagination and filtering.
   *
   * @param params - Query parameters for filtering and pagination
   * @returns Paginated list of organizations
   */
  async listOrganizations(params?: OrganizationListParams): Promise<PaginatedOrganizations> {
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.page_size) {
      queryParams.append('page_size', params.page_size.toString());
    }
    if (params?.search) {
      queryParams.append('search', params.search);
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }

    const url = queryParams.toString()
      ? `${this.getBaseUrl()}?${queryParams.toString()}`
      : this.getBaseUrl();

    return ApiService.request<PaginatedOrganizations>(url, {
      method: 'GET',
    });
  }

  /**
   * Get organization details.
   *
   * @param orgId - Organization ID
   * @returns Organization details with counts
   */
  async getOrganization(orgId: number): Promise<OrganizationWithCounts> {
    return ApiService.request<OrganizationWithCounts>(
      `${this.getBaseUrl()}/${orgId}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Create a new organization.
   *
   * @param data - Organization creation data
   * @returns Created organization
   */
  async createOrganization(data: OrganizationCreate): Promise<Organization> {
    return ApiService.request<Organization>(
      this.getBaseUrl(),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Update an organization.
   *
   * @param orgId - Organization ID
   * @param data - Fields to update
   * @returns Updated organization
   */
  async updateOrganization(orgId: number, data: OrganizationUpdate): Promise<Organization> {
    return ApiService.request<Organization>(
      `${this.getBaseUrl()}/${orgId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Delete an organization.
   *
   * @param orgId - Organization ID
   */
  async deleteOrganization(orgId: number): Promise<void> {
    await ApiService.request<void>(
      `${this.getBaseUrl()}/${orgId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * List tenants in an organization.
   *
   * @param orgId - Organization ID
   * @returns List of tenants
   */
  async listOrganizationTenants(orgId: number): Promise<OrganizationTenant[]> {
    return ApiService.request<OrganizationTenant[]>(
      `${this.getBaseUrl()}/${orgId}/tenants`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * List users in an organization.
   *
   * @param orgId - Organization ID
   * @returns List of users
   */
  async listOrganizationUsers(orgId: number): Promise<OrganizationUser[]> {
    return ApiService.request<OrganizationUser[]>(
      `${this.getBaseUrl()}/${orgId}/users`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * List all members in an organization (users + pending invitations).
   * Used for federated organizations to show a combined view of
   * active users and pending invitations.
   *
   * @param orgId - Organization ID
   * @returns List of organization members (users and invitations combined)
   */
  async listOrganizationMembers(orgId: number): Promise<OrganizationMember[]> {
    return ApiService.request<OrganizationMember[]>(
      `${this.getBaseUrl()}/${orgId}/members`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Resend invitation email and extend expiration for a pending invitation.
   * Used for federated organizations to resend invitations to users who
   * haven't accepted yet.
   *
   * @param orgId - Organization ID
   * @param invitationId - Invitation ID to resend
   * @returns Response indicating if invite was sent
   * @throws 400 if invitation has already been accepted
   * @throws 404 if invitation not found
   */
  async resendInvitation(orgId: number, invitationId: number): Promise<ResendInviteResponse> {
    return ApiService.request<ResendInviteResponse>(
      `${this.getBaseUrl()}/${orgId}/invitations/${invitationId}/resend`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Assign a user to an organization.
   *
   * @param orgId - Organization ID
   * @param userId - User ID to assign
   * @returns Updated user
   */
  async assignUserToOrganization(orgId: number, userId: number): Promise<OrganizationUser> {
    return ApiService.request<OrganizationUser>(
      `${this.getBaseUrl()}/${orgId}/users`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: userId }),
      }
    );
  }

  /**
   * Remove a user from an organization.
   *
   * @param orgId - Organization ID
   * @param userId - User ID to remove
   */
  async removeUserFromOrganization(orgId: number, userId: number): Promise<void> {
    await ApiService.request<void>(
      `${this.getBaseUrl()}/${orgId}/users/${userId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * Assign a tenant to an organization.
   *
   * @param orgId - Organization ID
   * @param tenantDbId - Tenant database ID to assign
   * @returns Updated tenant
   */
  async assignTenantToOrganization(orgId: number, tenantDbId: number): Promise<OrganizationTenant> {
    return ApiService.request<OrganizationTenant>(
      `${this.getBaseUrl()}/${orgId}/tenants`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenant_id: tenantDbId }),
      }
    );
  }

  /**
   * Remove a tenant from an organization.
   *
   * @param orgId - Organization ID
   * @param tenantDbId - Tenant database ID to remove
   */
  async removeTenantFromOrganization(orgId: number, tenantDbId: number): Promise<void> {
    await ApiService.request<void>(
      `${this.getBaseUrl()}/${orgId}/tenants/${tenantDbId}`,
      {
        method: 'DELETE',
      }
    );
  }

  /**
   * List users not assigned to any organization.
   *
   * @returns List of unassigned users
   */
  async listUnassignedUsers(): Promise<OrganizationUser[]> {
    return ApiService.request<OrganizationUser[]>(
      `${this.getBaseUrl()}/unassigned/users`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * List tenants not assigned to any organization.
   *
   * @returns List of unassigned tenants
   */
  async listUnassignedTenants(): Promise<OrganizationTenant[]> {
    return ApiService.request<OrganizationTenant[]>(
      `${this.getBaseUrl()}/unassigned/tenants`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Update a user's tenant access within an organization.
   *
   * @param orgId - Organization ID
   * @param userId - User ID to update
   * @param tenantIds - List of tenant_id GUIDs to grant access to
   * @returns Success message
   */
  async updateUserTenantAccess(
    orgId: number,
    userId: number,
    tenantIds: string[]
  ): Promise<{ message: string }> {
    return ApiService.request<{ message: string }>(
      `${this.getBaseUrl()}/${orgId}/users/${userId}/tenant-access`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ tenant_ids: tenantIds }),
      }
    );
  }

  /**
   * Get the admin consent URL for a federated organization.
   *
   * @param orgId - Organization ID
   * @returns Consent URL for the organization's tenant
   */
  async getConsentUrl(orgId: number): Promise<{ consent_url: string }> {
    return ApiService.request<{ consent_url: string }>(
      `${this.getBaseUrl()}/${orgId}/consent-url`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Get organization by primary domain.
   * This is a public endpoint used during the login flow to detect
   * if a user's email domain belongs to a federated organization.
   * No authentication required.
   *
   * @param domain - Domain to look up (e.g., "smithlaw.com")
   * @returns Public organization info if found
   * @throws 404 if domain not found or org is not federated
   */
  async getByDomain(domain: string): Promise<OrganizationPublicInfo> {
    return ApiService.request<OrganizationPublicInfo>(
      `${this.getBaseUrl()}/by-domain/${encodeURIComponent(domain)}`,
      {
        method: 'GET',
      }
    );
  }

  /**
   * Create a new user within an organization.
   * Used by org owners to pre-register users before they can login.
   *
   * @param orgId - Organization ID
   * @param data - User creation data (email, name, send_invite)
   * @returns Created user with invite status
   * @throws 403 if organization has reached max user limit
   * @throws 409 if user with this email already exists
   */
  async createOrgUser(orgId: number, data: CreateOrgUserRequest): Promise<CreatedOrgUser> {
    return ApiService.request<CreatedOrgUser>(
      `${this.getBaseUrl()}/${orgId}/users/create`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Resend invitation email to a user who hasn't logged in yet.
   *
   * @param orgId - Organization ID
   * @param userId - User ID to resend invite to
   * @returns Response indicating if invite was sent
   * @throws 400 if user has already logged in
   * @throws 403 if not organization owner
   * @throws 404 if user not found
   */
  async resendInvite(orgId: number, userId: number): Promise<ResendInviteResponse> {
    return ApiService.request<ResendInviteResponse>(
      `${this.getBaseUrl()}/${orgId}/users/${userId}/resend-invite`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Update an organization user's properties.
   * Used by org owners to update user name, role, or active status.
   *
   * @param orgId - Organization ID
   * @param userId - User ID to update
   * @param data - Fields to update (name, role, is_active)
   * @returns Updated user
   * @throws 403 if not organization owner
   * @throws 404 if user not found or not in organization
   */
  async updateOrgUser(
    orgId: number,
    userId: number,
    data: UpdateOrgUserRequest
  ): Promise<OrganizationUser> {
    return ApiService.request<OrganizationUser>(
      `${this.getBaseUrl()}/${orgId}/users/${userId}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Bulk delete multiple users from an organization.
   *
   * @param orgId - Organization ID
   * @param userIds - Array of user IDs to delete
   * @returns Response with deleted count and message
   * @throws 403 if not organization owner
   */
  async bulkDeleteUsers(
    orgId: number,
    userIds: number[]
  ): Promise<{ deleted_count: number; message: string }> {
    return ApiService.request<{ deleted_count: number; message: string }>(
      `${this.getBaseUrl()}/${orgId}/users/bulk`,
      {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_ids: userIds }),
      }
    );
  }
}

// Export singleton instance
export const OrganizationApiService = new OrganizationApiServiceClass();
